import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronDown, Loader2, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounce } from "@/lib/use-debounce";
import { cn } from "@/lib/utils";
import { helpdeskApi, type PharmacyName, useHelpdeskKey } from "./api";

/**
 * Wyszukiwarka apteki (`GET /helpdesk/pharmacies?q=`, max 30) — lista inline,
 * więc działa też w dialogu bez portalu. Wybór oddaje rodzicowi.
 */
export function PharmacySearch({
	onPick,
	exclude = [],
	autoFocus,
}: {
	onPick: (pharmacy: PharmacyName) => void;
	exclude?: number[];
	autoFocus?: boolean;
}) {
	const [q, setQ] = useState("");
	const debounced = useDebounce(q.trim(), 300);
	const key = useHelpdeskKey();
	const { data = [], isFetching } = useQuery({
		queryKey: key("pharmacies", debounced),
		queryFn: () => helpdeskApi.pharmacies(debounced),
		enabled: debounced.length >= 2,
		staleTime: 5 * 60_000,
	});
	const results = data.filter((p) => !exclude.includes(p.idfirm));

	return (
		<div className="space-y-1.5">
			<div className="relative">
				<Input
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder="Szukaj apteki (nazwa lub numer)…"
					aria-label="Szukaj apteki"
					className="h-8 text-xs"
					autoFocus={autoFocus}
				/>
				{isFetching ? (
					<Loader2 className="absolute top-2 right-2 size-4 animate-spin text-muted-foreground" />
				) : null}
			</div>
			{debounced.length >= 2 ? (
				<ul className="max-h-56 overflow-y-auto rounded-md border border-border">
					{results.map((p) => (
						<li key={p.idfirm}>
							<button
								type="button"
								className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/60"
								onClick={() => {
									onPick(p);
									setQ("");
								}}
							>
								<span className="font-mono tabular-nums text-muted-foreground">{p.idfirm}</span>
								<span className="truncate">{p.name}</span>
							</button>
						</li>
					))}
					{!isFetching && results.length === 0 ? (
						<li className="px-2 py-1.5 text-xs text-muted-foreground">Brak aptek</li>
					) : null}
				</ul>
			) : null}
		</div>
	);
}

/** Filtr apteki w pasku filtrów: przycisk z wybraną apteką i czyszczeniem. */
export function PharmacyFilter({
	idfirm,
	label,
	onChange,
	className,
}: {
	idfirm?: number;
	label?: string | null;
	onChange: (idfirm: number | undefined) => void;
	className?: string;
}) {
	const [open, setOpen] = useState(false);
	return (
		<div className={cn("flex items-center gap-1", className)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
						<Building2 className="size-3.5" aria-hidden />
						{idfirm ? (label ?? `Apteka ${idfirm}`) : "Apteka"}
						<ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
					</Button>
				</PopoverTrigger>
				<PopoverContent align="start" className="w-80 p-2">
					<PharmacySearch
						autoFocus
						onPick={(p) => {
							onChange(p.idfirm);
							setOpen(false);
						}}
					/>
				</PopoverContent>
			</Popover>
			{idfirm ? (
				<Button
					variant="ghost"
					size="icon"
					className="size-7"
					aria-label="Wyczyść filtr apteki"
					onClick={() => onChange(undefined)}
				>
					<X className="size-3.5" />
				</Button>
			) : null}
		</div>
	);
}
